import { tt } from '@agos/shared'
import type { FiskalSettings } from '@agos/api-client/model'

/**
 * Reading and writing one register's configuration.
 *
 * `GET /settings/fiskal` is typed `{ [key: string]: unknown }` in the spec, so the
 * narrowing happens here once rather than with a cast at each field. Every default
 * is legacy's, because a default on this screen decides how a register behaves
 * before anybody has touched it.
 */

export type KasaId = 'restauracia' | 'rozvoz'

export const KASA_IDS: readonly KasaId[] = ['restauracia', 'rozvoz'] as const

export interface ConnectionForm {
  isEnabled: boolean
  virtualMode: boolean
  serverUrl: string
  implementorId: string
  kasaHost: string
  kasaPort: number
  cardPaymentSlot: CardSlot
  /**
   * The FPAYI index for card **already collected** — at the door, or online.
   *
   * A different question from `cardPaymentSlot`, which is about a payment being taken now. The
   * register drives an integrated terminal on index 2, so a receipt for money already taken asks for
   * the card again unless this says otherwise.
   */
  notermPaymentSlot: string
}

/**
 * The shared block this screen can write - **and the three VAT rates are deliberately not in it**.
 *
 * §9.327. They were, and `readShared` defaulted the unset pair to legacy's 5 / 19. Both had to go
 * together with the pickers: the rates are now set on *Predaj a účtovanie - Sadzby DPH*, which saves
 * a **partial** body, and a form that still held them would send the values it read on mount - so
 * pressing *Uložiť zdieľané nastavenia* here after editing them there would quietly put the old ones
 * back. Removing the fields makes that impossible rather than merely unlikely.
 *
 * The defaulting was the sharper half: on a franchise with no rates stored, this screen would have
 * written 5 and 19 - numbers nobody chose - onto the row a receipt is printed from.
 */
export interface SharedForm {
  restaurantName: string
  restaurantAddress: string
  restaurantIco: string
  restaurantDic: string
}

export type CardSlot = '0' | '1' | '2'

/** The FiskalPRO N5's own default TCP port. */
export const DEFAULT_KASA_PORT = 6090

/** The HTTP bridge legacy ran before the direct-TCP transport, one port per register. */
export function defaultServerUrl(kasaId: KasaId): string {
  return kasaId === 'rozvoz' ? 'http://localhost:3457' : 'http://localhost:3456'
}

function text(payload: Record<string, unknown>, key: string): string {
  const value = payload[key]
  return typeof value === 'string' ? value : ''
}

function flag(payload: Record<string, unknown>, key: string): boolean {
  return payload[key] === true
}

export function readConnection(
  // Accepts the typed payload as well as a loose object: the reads below are by string key on
  // purpose, so the form survives a field being renamed on the server, and the tests build partials.
  // Spreading normalises both without a cast — `{ ...undefined }` is `{}`.
  payload: FiskalSettings | Record<string, unknown> | undefined,
  kasaId: KasaId,
): ConnectionForm {
  const source: Record<string, unknown> = { ...payload }
  const port = Number(source.kasaPort)

  return {
    isEnabled: flag(source, 'isEnabled'),
    virtualMode: flag(source, 'virtualMode'),
    serverUrl: text(source, 'serverUrl') || defaultServerUrl(kasaId),
    implementorId: text(source, 'implementorId'),
    kasaHost: text(source, 'kasaHost'),
    kasaPort: Number.isInteger(port) && port > 0 ? port : DEFAULT_KASA_PORT,
    cardPaymentSlot: readCardSlot(source.cardPaymentSlot),
    notermPaymentSlot: readNotermSlot(source.notermPaymentSlot),
  }
}

function readCardSlot(value: unknown): CardSlot {
  return value === '1' || value === '2' ? value : '0'
}

export function readShared(
  payload: FiskalSettings | Record<string, unknown> | undefined,
): SharedForm {
  const source: Record<string, unknown> = { ...payload }
  return {
    restaurantName: text(source, 'restaurantName'),
    restaurantAddress: text(source, 'restaurantAddress'),
    restaurantIco: text(source, 'restaurantIco'),
    restaurantDic: text(source, 'restaurantDic'),
  }
}


/**
 * The body for `PUT /settings/fiskal`.
 *
 * An untouched API-key box sends no `apiKey` at all — sending `''` would clear a
 * stored key, which is how a register stops authenticating after somebody saved
 * an unrelated field.
 */
export function connectionPayload(
  form: ConnectionForm,
  kasaId: KasaId,
  newApiKey: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    kasaId,
    isEnabled: form.isEnabled,
    virtualMode: form.virtualMode,
    serverUrl: form.serverUrl.trim(),
    implementorId: form.implementorId.trim(),
    kasaHost: form.kasaHost.trim(),
    kasaPort: form.kasaPort,
    cardPaymentSlot: form.cardPaymentSlot,
    notermPaymentSlot: form.notermPaymentSlot,
  }
  const key = newApiKey.trim()
  if (key) payload.apiKey = key
  return payload
}

export interface CardSlotOption {
  value: CardSlot
  label: string
  /** Why not to pick it, when there is a reason. */
  warning?: string
}

/**
 * The three ways a card payment can be driven, in legacy's order.
 *
 * Slot 1 carries a warning because legacy's own field hint says it is *broken on
 * the N5* — and the `rozvoz` register in the restored data is configured with it.
 * Legacy buries that in a hint below a select; here it is attached to the option
 * and shown when it is the one in force.
 */
export const CARD_SLOTS: readonly CardSlotOption[] = [
  { value: '0', label: tt('0 - bez FTCARDSTART (samostatný terminál)') },
  { value: '2', label: tt('2 - interný FTCARDSTART (integrovaný terminál)') },
  {
    value: '1',
    label: tt('1 - externý FTCARDSTART (starší režim)'),
    warning: tt('Na pokladni N5 tento režim nefunguje - použite 0 alebo 2.'),
  },
] as const

/** The indexes the server accepts — legacy's own constraint is one digit, 1–9. */
export const NOTERM_SLOTS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

/**
 * `2` when the server says nothing, because that is what the document builder hardcoded before the
 * setting existed — so a franchise that never opens this screen keeps exactly the behaviour it had.
 */
export function readNotermSlot(value: unknown): string {
  const text = typeof value === 'string' ? value.trim() : ''
  return (NOTERM_SLOTS as readonly string[]).includes(text) ? text : '2'
}

export function cardSlotWarning(slot: CardSlot): string | null {
  return CARD_SLOTS.find((option) => option.value === slot)?.warning ?? null
}

// ─── adding a register ───────────────────────────────────────────────────────

/**
 * What a register's id may be — `BACKEND.md` §10A.65, and the same rule the server enforces.
 *
 * <p>It reaches a device command and a URL, so it is a slug: two to thirty-two characters, lower
 * case, starting with a letter. Checked here so the save is disabled with the reason showing rather
 * than the person discovering it from a 400 after typing a name.
 */
export const KASA_ID_PATTERN = /^[a-z][a-z0-9_-]{1,31}$/

export type NewRegisterProblem = 'no-id' | 'bad-id' | 'duplicate'

export interface NewRegisterForm {
  kasaId: string
  label: string
}

export function newRegisterForm(): NewRegisterForm {
  return { kasaId: '', label: '' }
}

/**
 * Why this register cannot be added yet.
 *
 * <p>The duplicate check is client-side **as well as** server-side, and the server's is the one that
 * decides — this one only means the screen can say so before the round trip. The server answers 409;
 * it does not quietly edit the register that already exists.
 */
export function newRegisterProblems(
  form: NewRegisterForm,
  existingIds: readonly string[],
): NewRegisterProblem[] {
  const id = form.kasaId.trim().toLowerCase()
  if (id === '') return ['no-id']
  const problems: NewRegisterProblem[] = []
  if (!KASA_ID_PATTERN.test(id)) problems.push('bad-id')
  if (existingIds.some((existing) => existing.toLowerCase() === id)) problems.push('duplicate')
  return problems
}

export function canAddRegister(form: NewRegisterForm, existingIds: readonly string[]): boolean {
  return newRegisterProblems(form, existingIds).length === 0
}

export function newRegisterNote(problems: readonly NewRegisterProblem[]): string {
  if (problems.includes('duplicate')) return tt('Kasa s týmto id už existuje.')
  if (problems.includes('bad-id')) {
    return tt('Id: 2–32 znakov, malé písmená, číslice, - alebo _, začína písmenom.')
  }
  return tt('Id sa používa v príkazoch pre pokladňu, preto bez medzier a diakritiky.')
}

/** The body `POST /fiskal/registers` reads. An empty name is absent, not "". */
export function newRegisterPayload(form: NewRegisterForm): Record<string, unknown> {
  const label = form.label.trim()
  return label === ''
    ? { kasaId: form.kasaId.trim().toLowerCase() }
    : { kasaId: form.kasaId.trim().toLowerCase(), label }
}
